import { useMemo, useState } from 'react'
import { useData } from '../context/DataContext'
import { PageLoading } from '../components/common/LoadingSpinner'
import KPICard from '../components/common/KPICard'
import ChartCard from '../components/common/ChartCard'
import DataTable from '../components/common/DataTable'
import FilterDropdown from '../components/common/FilterDropdown'
import DateRangePicker from '../components/common/DateRangePicker'
import LineChart from '../components/charts/LineChart'
import StackedBarChart from '../components/charts/StackedBarChart'
import {
  CurrencyDollarIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon
} from '@heroicons/react/24/outline'
import {
  formatCurrency,
  formatPercent,
  formatMonth,
  getCountryName
} from '../utils/formatters'

export default function RevenueCountryReport() {
  const { data, computed, loading, error } = useData()
  const [selectedCountry, setSelectedCountry] = useState(null)
  const [startMonth, setStartMonth] = useState(null)
  const [endMonth, setEndMonth] = useState(null)

  const filteredData = useMemo(() => {
    if (!data || !computed) return null

    const { months } = computed
    const start = startMonth || months[Math.max(0, months.length - 12)]
    const end = endMonth || months[months.length - 1]
    const filteredMonths = months.filter(m => m >= start && m <= end)

    // Filter data by country if selected
    let monthlyArr = data.monthlyArr.filter(r =>
      filteredMonths.includes(r.month)
    )
    let movements = data.arrMovements.filter(m =>
      filteredMonths.includes(m.month)
    )

    if (selectedCountry) {
      monthlyArr = monthlyArr.filter(r => r.country === selectedCountry)
      movements = movements.filter(m => m.country === selectedCountry)
    }

    // ARR by month
    const arrByMonth = filteredMonths.map(month =>
      monthlyArr.filter(r => r.month === month).reduce((sum, r) => sum + r.arr_value, 0)
    )

    // Net New ARR by month
    const netNewArrByMonth = filteredMonths.map(month => {
      const monthMovements = movements.filter(m => m.month === month)
      return monthMovements.reduce((sum, m) => sum + m.arr_change, 0)
    })

    // Current metrics
    const latestMonth = filteredMonths[filteredMonths.length - 1]
    const previousMonth = filteredMonths[filteredMonths.length - 2]

    const currentArr = arrByMonth[arrByMonth.length - 1] || 0
    const previousArr = arrByMonth[arrByMonth.length - 2] || 0

    const periodMovements = movements.filter(m => m.month === latestMonth)
    const addedArr = periodMovements
      .filter(m => m.arr_change > 0)
      .reduce((sum, m) => sum + m.arr_change, 0)
    const lostArr = Math.abs(periodMovements
      .filter(m => m.arr_change < 0)
      .reduce((sum, m) => sum + m.arr_change, 0))

    // Movements by type for stacked chart
    const movementTypes = ['New Business', 'Expansion Upsell', 'Expansion Resell',
                          'Contraction Downsell', 'Contraction Resell', 'Churn']

    const movementDatasets = movementTypes.map(type => ({
      label: type,
      data: filteredMonths.map(month => {
        const val = movements
          .filter(m => m.month === month && m.movement_type === type)
          .reduce((sum, m) => sum + Math.abs(m.arr_change), 0)
        return type.includes('Contraction') || type === 'Churn' ? -val : val
      })
    }))

    // Movement table data
    const movementTableData = movements
      .sort((a, b) => b.month.localeCompare(a.month) || b.arr_change - a.arr_change)
      .map(m => ({
        ...m,
        id: m.movement_id
      }))

    return {
      months: filteredMonths,
      arrByMonth,
      netNewArrByMonth,
      movementDatasets,
      movementTableData,
      currentArr,
      previousArr,
      addedArr,
      lostArr
    }
  }, [data, computed, selectedCountry, startMonth, endMonth])

  if (loading) return <PageLoading />
  if (error) return <div className="text-red-500">Error: {error}</div>
  if (!filteredData) return null

  const {
    months,
    arrByMonth,
    netNewArrByMonth,
    movementDatasets,
    movementTableData,
    currentArr,
    previousArr,
    addedArr,
    lostArr
  } = filteredData

  const addedPercent = currentArr > 0 ? addedArr / currentArr : 0
  const lostPercent = currentArr > 0 ? lostArr / currentArr : 0

  return (
    <div className="space-y-6">
      {/* Filters */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex flex-wrap items-center gap-4">
          <FilterDropdown
            label="Country"
            value={selectedCountry}
            options={computed.countries.map(c => ({ value: c, label: getCountryName(c) }))}
            onChange={setSelectedCountry}
            placeholder="All Countries"
          />
        </div>
        <DateRangePicker
          startMonth={startMonth}
          endMonth={endMonth}
          months={computed.months}
          onStartChange={setStartMonth}
          onEndChange={setEndMonth}
        />
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        <KPICard
          title="Current ARR"
          value={formatCurrency(currentArr)}
          icon={CurrencyDollarIcon}
        />
        <KPICard
          title="Added ARR"
          value={formatCurrency(addedArr)}
          icon={ArrowTrendingUpIcon}
          colorScheme="success"
        />
        <KPICard
          title="Lost ARR"
          value={formatCurrency(lostArr)}
          icon={ArrowTrendingDownIcon}
          colorScheme="danger"
        />
        <KPICard
          title="Added ARR %"
          value={formatPercent(addedPercent)}
          colorScheme="success"
        />
        <KPICard
          title="Lost ARR %"
          value={formatPercent(lostPercent)}
          colorScheme="danger"
        />
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <ChartCard title="Annual Recurring Revenue" height="h-64">
          <LineChart
            labels={months}
            datasets={[{
              label: 'ARR',
              data: arrByMonth,
              color: '#3b82f6'
            }]}
            fill={true}
          />
        </ChartCard>

        <ChartCard title="Net New ARR" height="h-64">
          <LineChart
            labels={months}
            datasets={[{
              label: 'Net New ARR',
              data: netNewArrByMonth,
              color: '#22c55e'
            }]}
          />
        </ChartCard>
      </div>

      {/* Movement Table */}
      <ChartCard title="Movement per Month" height="h-auto">
        <DataTable
          columns={[
            { key: 'month', header: 'Month', render: v => formatMonth(v) },
            { key: 'customer_id', header: 'Alias', render: (v, row) => (
              <span className="text-brand-600 dark:text-brand-400 font-medium">
                {row.customer_id}
              </span>
            )},
            { key: 'organization_name', header: 'Organization' },
            { key: 'account_manager', header: 'Account Manager' },
            { key: 'country', header: 'Country', render: v => getCountryName(v) },
            { key: 'subscription_type', header: 'Subscription' },
            { key: 'revenue_stream', header: 'Revenue Stream' },
            { key: 'arr_change', header: 'ARR Change', render: v => (
              <span className={v >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}>
                {formatCurrency(v)}
              </span>
            )},
            { key: 'movement_type', header: 'Movement', render: v => (
              <span className={`badge ${
                v === 'New Business' || v.includes('Expansion') ? 'badge-success' :
                v === 'Churn' ? 'badge-danger' : 'badge-warning'
              }`}>
                {v}
              </span>
            )}
          ]}
          data={movementTableData}
          pageSize={10}
          searchable={true}
          searchPlaceholder="Search movements..."
        />
      </ChartCard>
    </div>
  )
}
